import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Artikel, ArtikelKomponente } from '../types';
import ArtikelForm from './ArtikelForm';
import KatalogSuchePanel from './KatalogSuchePanel';
import { useCustomFields } from '../hooks/useCustomFields';
import './ArtikelList.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ArtikelList: React.FC = () => {
  const [artikel, setArtikel] = useState<Artikel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingArtikel, setEditingArtikel] = useState<Artikel | null>(null);
  const [filterKategorie, setFilterKategorie] = useState<string>('');
  const [filterVariante, setFilterVariante] = useState<string>('');
  const [filterVerkauf, setFilterVerkauf] = useState<boolean>(false);
  const [zeigeInaktive, setZeigeInaktive] = useState<boolean>(false);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const authToken = localStorage.getItem('auth_token') || '';
  const { definitions: customFields } = useCustomFields('artikel', authToken);

  const loadArtikel = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKategorie) params.append('kategorie', filterKategorie);
      if (filterVariante) params.append('variante', filterVariante);
      if (filterVerkauf) params.append('nur_verkaufsartikel', 'true');
      params.append('nur_aktive', zeigeInaktive ? 'false' : 'true');
      const url = `${API_URL}/api/artikel?${params}`;
      const response = await axios.get(url);
      setArtikel(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der Artikel:', error);
    } finally {
      setLoading(false);
    }
  }, [filterKategorie, filterVariante, filterVerkauf, zeigeInaktive]);

  useEffect(() => {
    loadArtikel();
  }, [loadArtikel]);

  const handleEdit = (item: Artikel) => {
    setEditingArtikel(item);
    setShowForm(true);
  };

  const handleDeactivate = async (item: Artikel) => {
    if (!window.confirm(`Artikel "${item.bezeichnung}" deaktivieren? Er ist danach in neuen Belegen nicht mehr auswählbar, bestehende Daten bleiben erhalten.`)) return;
    try {
      await axios.delete(`${API_URL}/api/artikel/${item.artikel_id}`);
      loadArtikel();
    } catch (error) {
      console.error('Fehler beim Deaktivieren:', error);
    }
  };

  const handleActivate = async (item: Artikel) => {
    try {
      await axios.put(`${API_URL}/api/artikel/${item.artikel_id}/aktivieren`);
      loadArtikel();
    } catch (error) {
      console.error('Fehler beim Aktivieren:', error);
    }
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingArtikel(null);
    loadArtikel();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingArtikel(null);
  };

  const toggleExpand = (artikelId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(artikelId)) {
        next.delete(artikelId);
      } else {
        next.add(artikelId);
      }
      return next;
    });
  };

  const formatPrice = (price: number | null) => {
    if (price === null || price === undefined) return '-';
    return `${price.toFixed(2)} \u20AC`;
  };

  if (showForm) {
    return (
      <ArtikelForm
        editingArtikel={editingArtikel}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    );
  }

  return (
    <div className="artikel-list-container">
      <KatalogSuchePanel onUebernommen={loadArtikel} />
      <div className="list-header">
        <h2>Artikel</h2>
        <div className="header-actions">
          <select
            value={filterKategorie}
            onChange={(e) => setFilterKategorie(e.target.value)}
            className="filter-select"
          >
            <option value="">Alle Kategorien</option>
            <option value="ROHMATERIAL">Rohmaterial</option>
            <option value="HALBFERTIG">Halbfertig</option>
            <option value="FERTIGPRODUKT">Fertigprodukt</option>
            <option value="HILFSSTOFF">Hilfsstoff</option>
          </select>
          <input
            type="text"
            value={filterVariante}
            onChange={(e) => setFilterVariante(e.target.value)}
            className="filter-select"
            placeholder="Variante filtern..."
          />
          <label className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={filterVerkauf}
              onChange={(e) => setFilterVerkauf(e.target.checked)}
            />
            Nur Verkaufsartikel
          </label>
          <label className="filter-checkbox-label">
            <input
              type="checkbox"
              checked={zeigeInaktive}
              onChange={(e) => setZeigeInaktive(e.target.checked)}
            />
            Inaktive anzeigen
          </label>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            + Neuer Artikel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading">Lädt...</div>
      ) : artikel.length === 0 ? (
        <div className="empty-state">
          <p>Keine Artikel vorhanden</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Ersten Artikel erstellen
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="artikel-table">
            <thead>
              <tr>
                <th></th>
                <th>Bezeichnung</th>
                <th>Kategorie</th>
                <th>Variante</th>
                <th>Einheit</th>
                <th>Mindestbestand</th>
                <th>EK-Preis</th>
                <th>VK-Preis</th>
                <th>Qualität</th>
                {customFields.map(cf => (
                  <th key={cf.feld_id} title={cf.beschreibung || ''}>{cf.label}</th>
                ))}
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {artikel.map((item) => (
                <React.Fragment key={item.artikel_id}>
                  <tr className={`${item.ist_verkaufsartikel ? 'row-verkauf' : ''}${item.aktiv === false ? ' row-inaktiv' : ''}`}>
                    <td className="expand-cell">
                      {item.ist_verkaufsartikel && (item.komponenten?.length ?? 0) > 0 && (
                        <button
                          className="btn-expand"
                          onClick={() => toggleExpand(item.artikel_id)}
                          title="Stückliste anzeigen"
                        >
                          {expandedRows.has(item.artikel_id) ? '▼' : '▶'}
                        </button>
                      )}
                    </td>
                    <td className="artikel-name">
                      {item.bezeichnung}
                      {item.ist_verkaufsartikel && (
                        <span className="badge-verkauf" title="Dieser Artikel wird aktiv verkauft">VERKAUF</span>
                      )}
                    </td>
                    <td>
                      <span className="badge badge-kategorie">
                        {item.kategorie}
                      </span>
                    </td>
                    <td>{item.variante || '-'}</td>
                    <td>{item.einheit}</td>
                    <td>{item.mindestbestand}</td>
                    <td>{formatPrice(item.preis_ek)}</td>
                    <td>{formatPrice(item.preis_vk)}</td>
                    <td>{item.qualitaetsstufe || '-'}</td>
                    {customFields.map(cf => {
                      const val = ((item as any).custom_attributes || {})[cf.schluessel];
                      let display: string = '-';
                      if (val !== null && val !== undefined && val !== '') {
                        if (cf.typ === 'BOOLEAN') display = val ? '✓' : '–';
                        else display = String(val);
                      }
                      return <td key={cf.feld_id}>{display}</td>;
                    })}
                    <td className="actions-cell">
                      <button onClick={() => handleEdit(item)} className="btn-edit">
                        Bearbeiten
                      </button>
                      {item.aktiv === false ? (
                        <button onClick={() => handleActivate(item)} className="btn-edit">
                          Aktivieren
                        </button>
                      ) : (
                        <button onClick={() => handleDeactivate(item)} className="btn-delete">
                          Deaktivieren
                        </button>
                      )}
                    </td>
                  </tr>
                  {item.ist_verkaufsartikel && expandedRows.has(item.artikel_id) && (
                    <tr className="komponenten-row">
                      <td colSpan={10 + customFields.length}>
                        <div className="komponenten-panel">
                          <strong>Stückliste (benötigte Materialien):</strong>
                          {(item.komponenten?.length ?? 0) === 0 ? (
                            <span className="komponenten-leer"> Keine Komponenten hinterlegt</span>
                          ) : (
                            <table className="komponenten-table">
                              <thead>
                                <tr>
                                  <th>Material</th>
                                  <th>Menge</th>
                                  <th>Einheit</th>
                                </tr>
                              </thead>
                              <tbody>
                                {item.komponenten!.map((k: ArtikelKomponente) => (
                                  <tr key={k.id}>
                                    <td>{k.bezeichnung}</td>
                                    <td>{k.menge}</td>
                                    <td>{k.einheit}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ArtikelList;
